import { timeMinute, timeDay, CountableTimeInterval, timeHour } from 'd3-time';
import { BreaksRenderType, fallsWithinBreak } from './timebreaks';
import { RenderTimeRectFunction, TimeLineLabel } from './timeline';
import {
    TimeSeriesDisableRange,
    TimeSeriesDisableRangeLabel,
} from 'utils/timeseries/TimeSeriesDisableRange';
import { createDateTickFormatter } from './dateTickFormatter';
import { ensureXAxisLabelsDoNotOverlap } from 'utils/d3fc/xAxis';
import { AnySelection } from '@d3fc/d3fc-series';

/* eslint-disable @typescript-eslint/no-explicit-any */

export function renderXAxis(
    chart: any,
    id: string,
    width: number,
    displayXAxis: boolean,
    isDomainValid: boolean,
    startTime: number,
    endTime: number,
    xScale: any,
    timeBreaks: TimeSeriesDisableRange[],
    breaksType: BreaksRenderType,
    rangeLabels: TimeSeriesDisableRangeLabel[],
    timeLineLabels: TimeLineLabel[],
    renderTimeRect: RenderTimeRectFunction,
    showDate: boolean,
    configureTickIndexesToShow?: (
        selection: AnySelection<SVGElement>,
        tickIndexesToShow: number[]
    ) => void
) {
    applyCalculatedTicks(
        chart,
        isDomainValid,
        startTime,
        endTime,
        timeBreaks,
        breaksType,
        width
    );

    const xDecorate = (selection: any) => {
        if (!isDomainValid) {
            return;
        }

        selection
            .attr('visibility', (d: any) => {
                if (!displayXAxis) {
                    return 'hidden';
                }
                const nearbyBreak = getNearbyBreak(d, timeBreaks);

                if (nearbyBreak && !nearbyBreak.isMultiDayBreak) {
                    return 'hidden';
                }

                return 'visible';
            })
            .classed('is-multi-day-break', (d: any) => {
                const nearbyBreak = getNearbyBreak(d, timeBreaks);

                return nearbyBreak?.isMultiDayBreak;
            })
            .lower();

        renderTimeRect(
            selection,
            id,
            width,
            displayXAxis,
            xScale,
            startTime,
            endTime,
            isDomainValid,
            timeBreaks,
            breaksType,
            rangeLabels,
            timeLineLabels,
            showDate
        );
    };

    ensureXAxisLabelsDoNotOverlap(chart, xDecorate, configureTickIndexesToShow);
}

function applyCalculatedTicks(
    chart: any,
    isDomainValid: boolean,
    startTime: number,
    endTime: number,
    timeBreaks: TimeSeriesDisableRange[],
    breaksType: BreaksRenderType,
    width: number
) {
    const { tickSpacing, unitInterval } = computeTickInterval(
        startTime,
        endTime,
        width
    );
    const dayLabels = unitInterval === timeDay;

    chart
        .xTickValues(
            isDomainValid
                ? unitInterval
                      .every(tickSpacing)!
                      .range(new Date(startTime), new Date(endTime))
                      .filter(
                          (t: Date) =>
                              dayLabels ||
                              !fallsWithinBreak(
                                  t.getTime(),
                                  timeBreaks,
                                  breaksType
                              )
                      )
                      .concat(
                          timeBreaks.map((b) => b.from).map((t) => new Date(t)),
                          dayLabels ? [new Date(startTime)] : []
                      )
                : []
        )
        .xTickFormat(
            createDateTickFormatter(timeBreaks, dayLabels, tickSpacing)
        );
}

const HOUR_IN_MS = 60000 * 60;
function computeTickInterval(
    startTime: number,
    endTime: number,
    width: number
) {
    const timeSpan = endTime - startTime;

    const createTickInterval = (
        tickSpacing: number,
        unitInterval: CountableTimeInterval
    ) => ({ tickSpacing, unitInterval });

    if (timeSpan >= 16 * HOUR_IN_MS) {
        return createTickInterval(1, timeDay);
    }

    if (timeSpan >= 12 * HOUR_IN_MS) {
        return createTickInterval(2, timeHour);
    }
    if (timeSpan >= 6 * HOUR_IN_MS) {
        return createTickInterval(1, timeHour);
    }

    if (timeSpan >= 4 * HOUR_IN_MS && width < 350) {
        return createTickInterval(1, timeHour);
    }
    if (timeSpan >= 4 * HOUR_IN_MS) {
        return createTickInterval(30, timeMinute);
    }

    if (timeSpan >= 2 * HOUR_IN_MS && width < 350) {
        return createTickInterval(30, timeMinute);
    }
    if (timeSpan >= 2 * HOUR_IN_MS) {
        return createTickInterval(15, timeMinute);
    }

    if (timeSpan >= HOUR_IN_MS) {
        return createTickInterval(10, timeMinute);
    }
    if (timeSpan >= HOUR_IN_MS) {
        return createTickInterval(10, timeMinute);
    }

    if (timeSpan >= 0.5 * HOUR_IN_MS) {
        return createTickInterval(5, timeMinute);
    }
    if (timeSpan >= 0.25 * HOUR_IN_MS) {
        return createTickInterval(2, timeMinute);
    }

    return createTickInterval(1, timeMinute);
}

interface TimeSeriesDisableRangeWithMultiDayInfo
    extends TimeSeriesDisableRange {
    isMultiDayBreak: boolean;
}

export function getNearbyBreak(
    time: number | Date,
    breaks: TimeSeriesDisableRange[]
): TimeSeriesDisableRangeWithMultiDayInfo | undefined {
    const BreakPadding = 5 * 60000; // 5 mins
    const timeMs = time instanceof Date ? time.getTime() : time;
    const br = breaks.find(
        (b) => timeMs > b.from - BreakPadding && timeMs < b.to + BreakPadding
    );
    if (br) {
        return {
            ...br,
            isMultiDayBreak:
                new Date(br.from).getDay() !== new Date(br.to).getDay(),
        };
    }
    return undefined;
}
